/**
 * Splits clipboard text copied from a spreadsheet (Google Sheets, Excel) into rows of cells.
 *
 * Spreadsheets put a tab between cells and a newline between rows, and wrap a cell in double
 * quotes when it contains a tab, newline, or quote. The trailing newline Excel appends after the
 * last row is ignored.
 */
export const parseSpreadsheetClipboard = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let index = 0;
  const endRow = () => {
    row.push(cell);
    rows.push(row);
    row = [];
    cell = '';
  };
  while (index < text.length) {
    const char = text[index];
    if (char === '"' && cell === '') {
      // A quoted cell runs until a quote that is not doubled.
      index += 1;
      while (index < text.length) {
        if (text[index] === '"' && text[index + 1] === '"') {
          cell += '"';
          index += 2;
        } else if (text[index] === '"') {
          index += 1;
          break;
        } else {
          cell += text[index];
          index += 1;
        }
      }
      continue;
    }
    if (char === '\t') {
      row.push(cell);
      cell = '';
    } else if (char === '\r' || char === '\n') {
      if (char === '\r' && text[index + 1] === '\n') index += 1;
      endRow();
    } else {
      cell += char;
    }
    index += 1;
  }
  if (cell !== '' || row.length > 0) endRow();
  return rows;
};

/** Whether clipboard text holds more than one spreadsheet cell. */
export const isMultiCellClipboard = (text: string) => {
  const rows = parseSpreadsheetClipboard(text);
  return rows.length > 1 || (rows[0]?.length ?? 0) > 1;
};

/** Normalizes a pasted number by trimming it and dropping thousands separators, e.g. "1,200 ". */
export const normalizePastedNumber = (value: string) => value.trim().replace(/[,\s]/g, '');
